import { type ReactNode, useState } from 'react';

import { ChevronDown } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';

type Props = { title?: string; children: ReactNode; dataCyName?: string } & (
  | {
      isCollapsable: true;
      isCollapsed?: boolean;
      onToggleCollapse?: () => void;
    }
  | {
      isCollapsable?: false;
      isCollapsed?: boolean;
      onToggleCollapse?: () => void;
    }
);

export const KeyValue = (props: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    props.isCollapsed ?? false
  );

  const onToggleCollapse = () => {
    if (!props.isCollapsable) return;
    setIsCollapsed(prev => !prev);
    props.onToggleCollapse?.();
  };

  return (
    <div
      className="group flex w-full flex-col flex-nowrap gap-[5px]"
      data-cy={`key-value-wrapper-${props.dataCyName || props.title}`}
      data-is-collapsed={isCollapsed}
      data-testid="key-value-wrapper"
    >
      <div
        aria-hidden={!props.title}
        aria-label={
          props.isCollapsable ? 'Click to toggle collapse' : undefined
        }
        className={cn(
          'group/title flex items-center text-primary-deep',
          props.isCollapsable && 'cursor-pointer hover:brightness-90',
          !props.title && 'hidden'
        )}
        data-cy={`key-value-title-container-${props.dataCyName || props.title}`}
        data-testid="key-value-title-container"
        onClick={onToggleCollapse}
      >
        <div
          className="truncate font-semibold text-[10px] uppercase leading-[12px] tracking-[0.5px]"
          data-testid="key-value-title"
          title={props.title}
        >
          {props.title}
        </div>
        {props.isCollapsable && (
          <div
            className={cn(
              'ml-1 flex h-3 w-3 items-center justify-between transition-transform duration-300 ease-out group-hover/title:rotate-180',
              isCollapsed && 'rotate-180'
            )}
            data-testid="key-value-arrow-icon"
          >
            <ChevronDown size={14} />
          </div>
        )}
      </div>
      <div
        aria-hidden={isCollapsed}
        className={cn(
          'ml-2 flex min-h-[18px] flex-row flex-nowrap items-center truncate text-wrap border-border border-l-2 pl-4 text-card-foreground text-sm group-hover:border-primary',
          isCollapsed && 'hidden'
        )}
        data-cy={`key-value-content-container-${props.dataCyName || props.title}`}
        data-testid="key-value-content-container"
      >
        {props.children}
      </div>
    </div>
  );
};
